import BILLING_API from 'api/billing/v1'
import ORGANIZATION_API from 'api/organization/v1'
import PAYMENT_PROFILES_API from 'api/payment-profiles/v1'
import { push } from 'connected-react-router'
import { change, startSubmit, stopSubmit, SubmissionError } from 'redux-form'
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects'
import extractErrors from 'utils/extractErrors'
import { OPF, TAX_SYSTEM } from 'modules/Finance/PaymentProfiles/Create/dadataMapper'
import { ROUTES } from 'constants/routes'
import { generatePath } from 'react-router'
import { notificationShow } from 'modules/Notifications/actions'
import {
  FETCH_INVOICES,
  GET_ORGANIZATION_BANK,
  GET_ORGANIZATION_SUGGEST,
  GET_PAYMENT_PROFILES,
  PAYMENT_PROFILE_CREATE,
  PAYMENT_PROFILES_SET,
  PAYMENT_PROFILES_SET_LOADING,
  PAYMENTS_SET_LOADING,
  SET_PAYMENTS,
} from 'modules/Finance/PaymentProfiles/types'

function* fetchPaymentProfiles({ payload }) {
  try {
    yield put({
      type: PAYMENT_PROFILES_SET_LOADING,
      payload: { loading: true },
    })

    const { response, error } = yield call(PAYMENT_PROFILES_API.fetchProfiles, {
      params: payload.params,
    })

    if (error) {
      throw error
    }

    const { data, pagination } = response
    yield put({
      type: PAYMENT_PROFILES_SET,
      payload: { data, pagination },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: PAYMENT_PROFILES_SET_LOADING,
      payload: { loading: false },
    })
  }
}

function* fetchInvoices({ payload }) {
  try {
    yield put({
      type: PAYMENTS_SET_LOADING,
      payload: { loading: true },
    })

    const { response, error } = yield call(BILLING_API.fetchInvoices, {
      params: {
        ...payload.params,
        payment_profile_id: payload.id,
      },
    })

    if (error) {
      throw error
    }

    const { data, pagination } = response

    yield put({
      type: SET_PAYMENTS,
      payload: {
        data,
        pagination,
      },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: PAYMENTS_SET_LOADING,
      payload: { loading: false },
    })
  }
}

function* paymentProfileCreate({
  payload,
  meta: { resolve = () => {}, reject = () => {} } = {},
  formName,
  profileId,
}) {
  const { i18n } = yield select(({ app }) => app)

  try {
    yield put(startSubmit(formName))

    const { error } = yield call(
      profileId ? PAYMENT_PROFILES_API.update : PAYMENT_PROFILES_API.create,
      {
        profileId,
        data: payload,
      },
    )

    if (error) {
      throw error
    }

    yield put(push(generatePath(ROUTES.FINANCE_PAYMENT_PROFILES, { i18n })))

    yield put(
      notificationShow({
        type: 'success',
        message: 'payment_profiles::new.notifications.success',
        delay: 10000,
      }),
    )

    resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    reject(fields ? new SubmissionError(fields) : error.statusText)

    if (form) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
          delay: 10000,
        }),
      )
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* getOrganizationSuggest({ payload, formName, meta: { resolve = () => {} } = {} }) {
  const { asyncErrors } = yield select((state) => state['form'][formName])

  if (asyncErrors?.details?.tax_number) {
    resolve()
    return
  }

  try {
    const { response, error } = yield call(ORGANIZATION_API.suggestOrganization, {
      params: payload,
    })

    if (error) {
      throw error
    }

    if (response?.data) {
      const { name, opf, tax_system, kpp, ogrn, ogrnip, address, management } = response.data

      yield put(change(formName, 'details.organisation_name', name))
      yield put(change(formName, 'details.legal_personality', OPF?.[opf] ?? ''))
      yield put(change(formName, 'details.tax_system', TAX_SYSTEM?.[tax_system] ?? ''))

      if (kpp) {
        yield put(change(formName, 'details.kpp', kpp))
      }

      if (ogrn) {
        yield put(change(formName, 'details.ogrn', ogrn))
      }

      if (ogrnip) {
        yield put(change(formName, 'details.ogrnip', ogrnip))
      }

      if (address) {
        yield put(change(formName, 'details.juridical_address', address))
      }

      if (management) {
        yield put(change(formName, 'details.general_director', management))
      }
    }
  } catch (error) {
    console.logXHR(error)
  } finally {
    resolve()
  }
}

function* getOrganizationBank({ payload, formName, meta: { resolve = () => {} } = {} }) {
  const { asyncErrors } = yield select((state) => state['form'][formName])

  if (asyncErrors?.details?.bic) {
    resolve()
    return
  }

  try {
    const { response, error } = yield call(ORGANIZATION_API.fetchBankInfo, {
      params: payload,
    })

    if (error) {
      throw error
    }

    if (response?.data) {
      const { name, correspondent_account } = response.data

      yield put(change(formName, 'details.bank_name', name))
      yield put(change(formName, 'details.correspondent_account', correspondent_account))
    }
  } catch (error) {
    console.logXHR(error)
  } finally {
    resolve()
  }
}

export default function* Sagas() {
  yield takeLatest(GET_PAYMENT_PROFILES, fetchPaymentProfiles)
  yield takeLatest(FETCH_INVOICES, fetchInvoices)
  yield takeLatest(PAYMENT_PROFILE_CREATE, paymentProfileCreate)
  yield debounce(500, GET_ORGANIZATION_SUGGEST, getOrganizationSuggest)
  yield debounce(500, GET_ORGANIZATION_BANK, getOrganizationBank)
}
